import LikeHeartIcon from '@/assets/svg/satisfaction-survey-like-heart.svg?raw'
import CheckmarkIcon from '@/assets/svg/satisfaction-survey-checkmark-circle-outline.svg?raw'
import SatisfactionSurveyAlert from '@/components/exames/satisfaction-survey-alert/index.vue'
import SatisfactionSurveyModal from '@/components/exames/satisfaction-survey-modal/index.vue'
import SurveyMixin from '@/shared/mixins/survey.mixin'

const satisfactionSurveyScoreOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const satisfactionSurveyScoreColors = [
  '#B72224',
  '#D52029',
  '#E95223',
  '#EA6F22',
  '#F6A726',
  '#FDC729',
  '#EBDB0A',
  '#E5E044',
  '#C2D234',
  '#AEC93C',
  '#66B44E'
]

export default {
  name: 'satisfaction-survey',

  mixins: [
    SurveyMixin
  ],

  components: {
    SatisfactionSurveyAlert,
    SatisfactionSurveyModal
  },

  props: {
    surveyDisplayOptions: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      activeSurvey: null,
      isSurveyAlertOpen: false,
      isSurveyModalOpen: false,
      surveyAnswers: {},
      submittedSurveyScore: null,
      isLoadingSurvey: false,
      isSubmittingSurvey: false,
      isSuccessFeedbackOpen: false,
      scoreOptions: satisfactionSurveyScoreOptions,
      scoreColors: satisfactionSurveyScoreColors,
      likeHeartIcon: LikeHeartIcon,
      checkmarkIcon: CheckmarkIcon
    }
  },

  computed: {
    surveyQuestions () {
      return this.activeSurvey === null ? [] : this.activeSurvey.questions
    },

    isConfirmButtonDisabled () {
      if (this.isSubmittingSurvey === true) {
        return true
      }

      const hasRequiredQuestionWithoutAnswer = this.surveyQuestions.some((question) => {
        return question.required && !this.isQuestionAnswered(question)
      })

      if (hasRequiredQuestionWithoutAnswer) {
        return true
      }

      return !this.surveyQuestions.some((question) => this.isQuestionAnswered(question))
    }
  },

  watch: {
    surveyDisplayOptions: {
      immediate: true,
      handler () {
        this.showSurveyAlertIfItShouldBeShown()
      }
    }
  },

  methods: {
    showSurveyAlertIfItShouldBeShown () {
      if (!this.surveyDisplayOptions?.show || !this.surveyDisplayOptions?.surveyId) {
        this.clearSurveyState()
        return
      }

      this.activeSurvey = null
      this.isSurveyAlertOpen = true
      this.isSurveyModalOpen = false
      this.isSuccessFeedbackOpen = false
      this.surveyAnswers = {}
      this.submittedSurveyScore = null
      this.emitSurveyAlertVisibility()
    },

    clearSurveyState () {
      this.activeSurvey = null
      this.isSurveyAlertOpen = false
      this.isSurveyModalOpen = false
      this.isSuccessFeedbackOpen = false
      this.isLoadingSurvey = false
      this.surveyAnswers = {}
      this.submittedSurveyScore = null
      this.emitSurveyAlertVisibility()
    },

    normalizeSurvey (survey) {
      const questions = survey.questions || survey.Questions || []

      return {
        surveyId: survey.surveyId || survey.SurveyId || survey.id || survey.Id,
        mandatory: survey.mandatory === true || survey.Mandatory === true || this.surveyDisplayOptions?.mandatory === true,
        versionSurvey: survey.versionSurvey || survey.VersionSurvey || null,
        title: survey.title || survey.Title || '',
        questions: questions
          .map((question) => this.normalizeSurveyQuestion(question))
          .sort((firstQuestion, secondQuestion) => firstQuestion.order - secondQuestion.order)
      }
    },

    normalizeSurveyQuestion (question) {
      return {
        required: question.required === true || question.Required === true,
        questionId: question.questionId || question.QuestionId || question.id || question.Id,
        order: Number(question.order || question.Order || 0),
        text: question.text || question.Text || question.question || question.Question || '',
        type: this.normalizeSurveyQuestionType(question.type || question.Type || question.questionType || question.QuestionType)
      }
    },

    normalizeSurveyQuestionType (type) {
      const normalizedType = `${type || ''}`
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')

      switch (normalizedType) {
        case 'nota':
        case 'score':
          return 'nota'
        case 'confirmacao':
        case 'confirmation':
          return 'confirmacao'
        case 'sugestao':
        case 'suggestion':
          return 'sugestao'
        default:
          return normalizedType
      }
    },

    initializeSurveyAnswers () {
      const answers = {}

      this.surveyQuestions.forEach((question) => {
        answers[question.questionId] = {
          questionId: question.questionId,
          order: question.order,
          score: null,
          sugestion: '',
          confirm: null
        }
      })

      this.surveyAnswers = answers
      this.submittedSurveyScore = null
    },

    selectSurveyScore ({ questionId, score }) {
      const answer = this.getSurveyAnswer(questionId)

      answer.score = answer.score === score ? null : score
      this.$set(this.surveyAnswers, questionId, answer)
    },

    selectConfirmationAnswer ({ questionId, confirm }) {
      const answer = this.getSurveyAnswer(questionId)

      answer.confirm = answer.confirm === confirm ? null : confirm
      this.$set(this.surveyAnswers, questionId, answer)
    },

    updateSurveyComment ({ questionId, comment }) {
      const answer = this.getSurveyAnswer(questionId)

      answer.sugestion = comment
      this.$set(this.surveyAnswers, questionId, answer)
    },

    getSurveyAnswer (questionId) {
      return this.surveyAnswers[questionId] || {
        questionId,
        order: '',
        score: null,
        sugestion: '',
        confirm: null
      }
    },

    isQuestionAnswered (question) {
      const answer = this.getSurveyAnswer(question.questionId)

      switch (question.type) {
        case 'nota':
          return answer.score !== null
        case 'confirmacao':
          return answer.confirm !== null
        case 'sugestao':
          return answer.sugestion.trim() !== ''
        default:
          return false
      }
    },

    getFirstSubmittedScore () {
      const scoreQuestion = this.surveyQuestions.find((question) => question.type === 'nota')

      if (!scoreQuestion) {
        return null
      }

      return this.getSurveyAnswer(scoreQuestion.questionId).score
    },

    buildSurveyAnswersPayload () {
      return this.surveyQuestions.filter((question) => this.isQuestionAnswered(question)).map((question) => {
        const answer = this.getSurveyAnswer(question.questionId)

        return {
          questionId: question.questionId,
          score: answer.score,
          sugestion: answer.sugestion,
          confirm: answer.confirm,
          order: `${question.order}`
        }
      })
    },

    async openSurveyModalFromAlert () {
      if (this.isLoadingSurvey || !this.surveyDisplayOptions?.surveyId) {
        return
      }

      try {
        this.isLoadingSurvey = true
        const surveyResponse = await this.getSurveyToAnswer(this.surveyDisplayOptions.surveyId)
        this.activeSurvey = this.normalizeSurvey(surveyResponse)
        this.initializeSurveyAnswers()
        this.isSurveyModalOpen = this.surveyQuestions.length > 0
      } catch (error) {
        this.$root.$emit('toast', { message: this.$i18n.t('satisfactionSurvey.error.load'), type: 'error' })
      } finally {
        this.isLoadingSurvey = false
      }
    },

    closeSurveyModal () {
      this.isSurveyModalOpen = false
    },

    async confirmSurveyAnswer () {
      if (this.activeSurvey === null || this.isConfirmButtonDisabled) {
        return
      }

      const surveyAnswerPayload = {
        surveyId: this.activeSurvey.surveyId,
        skipped: false,
        mandatory: this.activeSurvey.mandatory,
        versionSurvey: this.activeSurvey.versionSurvey,
        answers: this.buildSurveyAnswersPayload()
      }

      try {
        this.isSubmittingSurvey = true
        await this.submitSurveyAnswers(surveyAnswerPayload)
        this.submittedSurveyScore = this.getFirstSubmittedScore()
        this.isSurveyAlertOpen = false
        this.emitSurveyAlertVisibility()
        this.isSurveyModalOpen = false
        this.isSuccessFeedbackOpen = true
      } catch (error) {
        this.$root.$emit('toast', { message: this.$i18n.t('satisfactionSurvey.error.submit'), type: 'error' })
      } finally {
        this.isSubmittingSurvey = false
      }
    },

    closeSuccessFeedback () {
      this.isSuccessFeedbackOpen = false
    },

    emitSurveyAlertVisibility () {
      this.$emit('survey-alert-visibility-changed', this.isSurveyAlertOpen)
    }
  }
}
